import { lazy, Suspense, useMemo, useEffect, useState } from "react";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { useIsHomeDisplayReady } from "@/modules/homeDisplay/hooks";
import { useUserDataFlattened } from "@/modules/user/stores";
import { getVipList } from "@/api/vip";
import type { CheckInDialogResult } from "./useCheckInDialog.types";

const CheckInDialog = lazy(() =>
  import("../components/CheckInDialog/CheckInDialog").then(m => ({
    default: m.CheckInDialog,
  }))
);

export const useCheckInDialog = (): CheckInDialogResult => {
  const { data: userInfo, isAuthenticated, loadUserCheckin } =
    useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();

  // 签到已移出 user_info，由本组件(全局 GlobalEvent)挂载时按需拉取(登录用户)
  useEffect(() => {
    if (isAuthenticated) loadUserCheckin().catch(() => {});
  }, [isAuthenticated, loadUserCheckin]);
  const [vipLevelList, setVipLevelList] = useState<any[]>([]);
  const [vipListUserCheckIn, setVipListUserCheckIn] = useState<any>(null);

  // 获取活动配置中的 priority，不存在则使用默认值
  const priority = POPUP_CONFIGS.CHECK_IN.priority;

  const dayAmounts = useMemo(
    () => userInfo?.bonusTaskCheckInDay?.checkInData?.dayAmounts || [],
    [userInfo?.bonusTaskCheckInDay?.checkInData?.dayAmounts]
  );

  const todayChecked = useMemo(() => {
    return userInfo?.userCheckIn?.data?.todayCheckIn || false;
  }, [userInfo?.userCheckIn?.data?.todayCheckIn]);

  // 获取 VIP list 数据（含完整 dayHistory），用于弹窗展示正确角标和金额
  useEffect(() => {
    if (!isAuthenticated || todayChecked) return;
    getVipList()
      .then(resp => {
        const levelList = resp?.data?.data?.levelList ?? [];
        setVipLevelList(levelList);
        // 找到有 userCheckIn 记录的那一层（各层 userCheckIn 是同一条记录）
        const levelWithCheckIn = levelList.find((l: any) => l.userCheckIn);
        if (levelWithCheckIn) {
          setVipListUserCheckIn(levelWithCheckIn.userCheckIn);
        }
      })
      .catch(() => {});
  }, [isAuthenticated, todayChecked]);

  const CheckInDialogContent: React.FC<{ onComplete: () => void }> = ({
    onComplete,
  }) => {
    return (
      <Suspense fallback={null}>
        <CheckInDialog
          onComplete={onComplete}
          levelList={vipLevelList}
          vipListUserCheckIn={vipListUserCheckIn}
        />
      </Suspense>
    );
  };

  const { trigger, remove } = useQueuedPopup({
    popupId: POPUP_CONFIGS.CHECK_IN.id,
    priority,
    shouldShow: () => {
      if (!isConfigReady) return false;
      if (!userInfo || !isAuthenticated) return false;
      if (todayChecked) return false;
      if (dayAmounts.length === 0) return false;
      return true;
    },
    renderContent: onComplete => (
      <CheckInDialogContent onComplete={onComplete} />
    ),
    showOnce: true,
    allowManualTrigger: false,
    dependencies: [
      isConfigReady,
      isAuthenticated,
      dayAmounts.length,
      todayChecked,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: "ENTER_HOME_PAGE",
  });

  useEffect(() => {
    if (todayChecked) {
      remove();
    }
  }, [todayChecked, remove]);

  return { trigger };
};
